import Deferred from 'deferred.js';
import events from 'events.js';

class Map {
    constructor(mapElem) {
        this.mapElem = mapElem;
        this.geocoder = new google.maps.Geocoder();
        this.bounds = new google.maps.LatLngBounds();
        this.addressDeferreds = [];
        this.markerDeferreds = [];
        this.markerImages = [];
        for (let i = 0; i < 9; i++) {
            this.markerImages[i] = {
                url: window.STATIC_URL + 'images/sprite-map.png',
                size: new google.maps.Size(20, 30),
                origin: new google.maps.Point(i * 20, 0),
                anchor: new google.maps.Point(0, 30),
            };
        }
        this.defaultMarkerImage = {
            url: window.STATIC_URL + 'images/sprite-map.png',
            size: new google.maps.Size(20, 30),
            origin: new google.maps.Point(180, 30),
            anchor: new google.maps.Point(0, 30),
        };
        this.mapOptions = {
            disableDefaultUI: true,
            scrollwheel: false,
            zoomControl: true,
            zoom: 16,
            zoomControlOptions: {
                style: google.maps.ZoomControlStyle.LARGE,
                position: google.maps.ControlPosition.RIGHT_CENTER,
            },
        };
        if (mapElem.getAttribute('data-zoom')) {
            this.mapOptions.zoom = parseInt(mapElem.getAttribute('data-zoom'));
        }
        this.map = new google.maps.Map(mapElem, this.mapOptions);
        this.locations = [];
        this.markers = [];
        this.markerElements = {};
    }

    async addElement(element) {
        const addr = element.getAttribute('data-address');
        const counter =
            parseInt(element.getAttribute('data-map-counter')) || 0;
        // Track when this finishes in a deferred so map rendering
        // kicks off when all markers done and not just when all addresses
        // have resolved
        const markerDeferred = new Deferred();
        this.markerDeferreds.push(markerDeferred);
        const addressDeferred = this.getAddressLatLong(addr);
        this.addressDeferreds.push(addressDeferred);

        try {
            const location = await addressDeferred;
            this.locations.push(location);
            this.bounds.extend(location);
            this.addMarker(element, location, counter);
            markerDeferred.resolve();
        } catch (ex) {
            markerDeferred.reject();
        }
    }

    async backoff(time) {
        return new Promise((resolve) => setTimeout(resolve, time));
    }

    async geocode(address) {
        return new Promise((resolve, reject) => {
            this.geocoder.geocode({address}, (results, status) => {
                if (
                    status === google.maps.GeocoderStatus.OK &&
                    results.length
                ) {
                    resolve(results[0].geometry.location);
                } else {
                    reject(Error(status));
                }
            });
        });
    }

    async getAddressLatLong(address) {
        let tries = 0;
        let message;
        while (tries < 3) {
            tries++;
            try {
                return await this.geocode(address);
            } catch (e) {
                message = e.message;
                await this.backoff(tries * 2500);
            }
        }
        return Promise.reject(Error(message));
    }

    highlight(elem) {
        const parent = elem.parentNode;
        for (const child of parent.childNodes) {
            if (!child.className) {
                continue;
            }
            child.classList.remove('highlight');
            parent.insertBefore(elem, parent.childNodes[0]);
        }
        elem.classList.add('highlight');
    }

    addMarker(element, location, counter) {
        let title = '';
        const titleElement = document.querySelector('[data-map-title]');
        if (titleElement && titleElement.length) {
            title = titleElement[0].innerHTML;
        }

        const icon = this.markerImages[counter - 1] || this.defaultMarkerImage;

        const marker = new google.maps.Marker({
            position: location,
            map: this.map,
            title,
            icon,
        });

        this.markerElements[element.id] = marker;
        this.markers.push(marker);

        google.maps.event.addListener(marker, 'click', () => {
            this.highlight(element);
        });
    }

    render() {
        const handler = () => {
            if (this.locations.length > 1) {
                this.map.fitBounds(this.bounds);
            } else {
                this.map.setCenter(this.locations[0]);
            }
            // In practice page pan map to allow space for location detail text
            const lD = document.getElementsByClassName('location-detail')[0];
            if (lD) {
                this.map.panBy(-lD.offsetWidth / 2, 0);
            }
        };
        // Rerender map with marker if address is found
        this.markerDeferreds.forEach((marker) => {
            marker.then(handler, () => {});
        });
    }
}

function loadMaps(key) {
    const p = new Promise((resolve) => (window.initMap = resolve));
    const nonceEl = document.querySelector('[nonce]');
    const script = document.createElement('script');
    script.async = true;
    // eslint-disable-next-line max-len
    script.type = 'text/javascript';
    script.setAttribute(
        'nonce',
        nonceEl.nonce || scriptEl.getAttribute('nonce'),
    );
    script.src = `https://maps.googleapis.com/maps/api/js?v=3.exp&key=${key}&callback=initMap`;
    document.head.appendChild(script);
    return p;
}

(async function () {
    await events.ready();
    await loadMaps(document.body.dataset.googleMapsApiKey);
    const maps = {};

    // Collect all maps on the page
    for (const elem of Array.from(document.getElementsByClassName('js-map'))) {
        maps[elem.id] = new Map(elem);
    }

    const adds = [];
    // Add all address elements to each map
    for (const elem of document.querySelectorAll('*[data-map]')) {
        const map = maps[elem.getAttribute('data-map')];
        adds.push(map.addElement(elem));
    }

    // Now render each map
    Object.values(maps).forEach((m) => m.render());

    // Highlight Selected Location
    for (const elem of document.querySelectorAll('.location-list li')) {
        events.listen(elem, 'click', () => {
            elem.classList.remove('active');
        });
    }

    // center Marker
    const mapLocationList = document.querySelectorAll(
        '[data-map-location-list]',
    )[0];
    const map = maps.map;

    function centerMarker(marker) {
        const markerPosition = marker.getPosition();
        map.map.setCenter(markerPosition);
        if (!mapLocationList.parentNode.className.includes('profile-map')) {
            map.map.panBy(-mapLocationList.offsetWidth / 2, 0);
        }

        for (const ma of map.markers) {
            ma.setAnimation(null);
        }
        marker.setAnimation(google.maps.Animation.BOUNCE);
        setTimeout(function () {
            marker.setAnimation(null);
        }, 2100);
        // 2100 should be just enough for 3 bounces
    }

    for (const el of document.querySelectorAll('.location-list a')) {
        events.listen(el, 'click', (e) => {
            e.preventDefault();
            const id = el.getAttribute('data-location');
            const elem = document.getElementById(`practice-${id}`);
            const marker = map.markerElements[elem.id];
            map.highlight(elem);
            centerMarker(marker);
        });
    }

    const hideBtn = document.getElementsByClassName('map-toggle-btn')[0];
    if (hideBtn) {
        events.listen(hideBtn, 'click', () => {
            const mapArea = document.getElementById('map');
            const mapWrapper = document.getElementById('map-wrapper');
            const btnI = hideBtn.getElementsByTagName('i')[0];
            if (mapArea.className.includes('open')) {
                btnI.classList.remove('fa-angle-up');
                btnI.classList.add('fa-angle-down');
                mapArea.classList.remove('open');
                const selector = '.profile-map ul li';
                for (const el of document.querySelectorAll(selector)) {
                    el.classList.add('open');
                }
                mapWrapper.style.height = 0;
            } else {
                btnI.classList.remove('fa-angle-down');
                btnI.classList.add('fa-angle-up');
                let height = '300px';
                if (mapArea.className.includes('profile-map')) {
                    height = '180px';
                }
                mapWrapper.style.height = height;
                mapArea.classList.add('open');
            }
        });
    }

    const showDetails = Array.from(
        document.getElementsByClassName('show-details'),
    );
    for (const el of showDetails) {
        events.listen(el, 'click', () => {
            const sE = el.getElementsByClassName('status')[0];
            sE.innerText = sE.innerText === 'View ' ? 'Hide ' : 'View ';
            for (const cN of el.parentNode.childNodes) {
                if (cN.className && cN.className.includes('extra-info')) {
                    if (cN.style.display === 'none') {
                        cN.style.display = 'block';
                    } else {
                        cN.style.display = 'none';
                    }
                }
            }
            for (const elm of Array.from(el.getElementsByTagName('i'))) {
                if (elm.className.includes('fa-angle-down')) {
                    elm.classList.remove('fa-angle-down');
                    elm.classList.add('fa-angle-up');
                } else if (elm.className.includes('fa-angle-up')) {
                    elm.classList.remove('fa-angle-up');
                    elm.classList.add('fa-angle-down');
                }
            }
        });
    }
})();
